/**
 * Vault Hire — Compliance & Security Hub
 *
 * Role-gated workspace for designated DPO / InfoSec / Security Owner users
 * inside a customer organisation. Tab-based UX modelled on the Candidate
 * Vault. Org-scoped data via the user's `company_name` (single-tenant for
 * now; tenant_id will replace this in the multi-tenant migration).
 *
 * Six tabs:
 *   Overview · Agreements · AI governance · Audit trail · Documents · Sub-processor changes
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, FileSignature, Bot, ScrollText, FolderUp, GitBranch,
  CheckCircle2, AlertTriangle, Upload, Trash2, ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'overview',     label: 'Overview',     Icon: ShieldCheck },
  { id: 'agreements',   label: 'Agreements',   Icon: FileSignature },
  { id: 'ai',           label: 'AI governance', Icon: Bot },
  { id: 'audit',        label: 'Audit trail',  Icon: ScrollText },
  { id: 'documents',    label: 'Documents',    Icon: FolderUp },
  { id: 'subp',         label: 'Sub-processors', Icon: GitBranch },
];

function fmt(d) { return d ? d.replace('T', ' ').slice(0, 19) : '—'; }

function Pill({ tone = 'neutral', children, testId }) {
  const tones = {
    neutral: 'bg-[var(--vh-bg-muted)] text-[var(--vh-text-secondary)] border-[var(--vh-border)]',
    success: 'bg-[var(--vh-success)]/15 text-[var(--vh-success)] border-[var(--vh-success)]/30',
    warn: 'bg-[var(--vh-warning)]/30 text-[var(--vh-text-primary)] border-[var(--vh-warning)]',
    accent: 'bg-[var(--vh-accent)]/12 text-[var(--vh-accent)] border-[var(--vh-accent)]/30',
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-[0.12em] font-semibold ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

function StatCard({ label, value, sub, testId }) {
  return (
    <div className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--vh-accent)] mb-2">{label}</p>
      <p className="font-display text-3xl text-[var(--vh-text-primary)] leading-none">{value}</p>
      {sub && <p className="text-xs text-[var(--vh-text-muted)] mt-2">{sub}</p>}
    </div>
  );
}

function OverviewTab({ hub }) {
  if (!hub) return null;
  const s = hub.summary;
  const dpa = s.dpa_status;
  const dpaTone = dpa === 'countersigned' ? 'success' : (dpa === 'submitted' ? 'warn' : 'neutral');
  return (
    <div className="space-y-6" data-testid="hub-overview">
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="DPA status"
          value={dpa === 'not_submitted' ? '—' : dpa.replace('_', ' ')}
          sub={s.dpa_submitted_at ? `Submitted ${fmt(s.dpa_submitted_at)}` : 'Not submitted yet'}
          testId="hub-stat-dpa"
        />
        <StatCard
          label="Documents uploaded"
          value={s.documents_uploaded}
          sub="DPIAs, FRIAs, certs"
          testId="hub-stat-docs"
        />
        <StatCard
          label="AI workflows enabled"
          value={`${s.ai_workflows_enabled} / ${s.ai_workflows_total}`}
          sub={s.ai_global_enabled ? 'Org master switch ON' : 'Org master switch OFF'}
          testId="hub-stat-ai"
        />
        <StatCard
          label="Audit events visible"
          value={s.audit_events_total}
          sub="Scoped to your organisation"
          testId="hub-stat-audit"
        />
      </div>
      <div className="rounded-3xl p-7 md:p-9 bg-[var(--vh-brand-primary)] text-white">
        <p className="text-xs uppercase tracking-[0.22em] text-white/70 mb-2">Your role</p>
        <h3 className="font-display text-2xl md:text-3xl mb-2">{hub.viewer.compliance_role_label || (hub.viewer.role === 'owner' || hub.viewer.role === 'admin' ? 'Vault Hire administrator' : '—')}</h3>
        <p className="text-sm text-white/80 leading-relaxed">
          {hub.viewer.company_name ? `Scope: ${hub.viewer.company_name}.` : 'Internal view — you see all organisations.'}
          {' '}You may upload procurement artefacts (DPIA, FRIA, pen-test, certs), download your audit trail, and acknowledge sub-processor change notices on behalf of {hub.viewer.company_name || 'Vault Hire'}.
        </p>
      </div>
      <div className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-6">
        <p className="text-xs uppercase tracking-[0.16em] text-[var(--vh-accent)] mb-3">Quick links</p>
        <div className="flex flex-wrap gap-2">
          <a href="/legal/" target="_blank" rel="noreferrer" className="text-sm px-4 py-2 rounded-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)] inline-flex items-center gap-2"><ExternalLink className="w-3.5 h-3.5" />Legal library</a>
          <a href="/legal/data-processing-agreement.html#submit-signed" target="_blank" rel="noreferrer" className="text-sm px-4 py-2 rounded-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)] inline-flex items-center gap-2"><ExternalLink className="w-3.5 h-3.5" />Submit signed DPA</a>
          <Link to="/settings/ai-preferences" className="text-sm px-4 py-2 rounded-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)] inline-flex items-center gap-2">My AI preferences</Link>
          <a href="/legal/dpia-template.html" target="_blank" rel="noreferrer" className="text-sm px-4 py-2 rounded-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)] inline-flex items-center gap-2"><ExternalLink className="w-3.5 h-3.5" />DPIA template</a>
        </div>
      </div>
    </div>
  );
}

function AgreementsTab() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/compliance-hub/agreements`).then((r) => setItems(r.data.agreements)).catch(() => setItems([]));
  }, []);
  if (!items) return <div className="text-sm text-[var(--vh-text-secondary)]">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="hub-agreements">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--vh-text-secondary)]">Counter-signed DPA copies submitted by your organisation.</p>
        <a href="/legal/data-processing-agreement.html#submit-signed" target="_blank" rel="noreferrer" className="text-sm px-4 py-2 rounded-full bg-[var(--vh-accent)] text-white hover:bg-[var(--vh-accent-hover)] inline-flex items-center gap-2 font-semibold" data-testid="hub-upload-dpa">
          <Upload className="w-4 h-4" /> Submit counter-signed DPA
        </a>
      </div>
      {items.length === 0 ? (
        <div className="bg-[var(--vh-bg-surface)] border border-dashed border-[var(--vh-border)] rounded-2xl p-10 text-center" data-testid="hub-agreements-empty">
          <FileSignature className="w-6 h-6 mx-auto mb-3 text-[var(--vh-text-muted)]" />
          <p className="text-sm text-[var(--vh-text-secondary)]">No DPA submissions on file yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5">
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="font-semibold text-[var(--vh-text-primary)]">{it.signer_name} · <span className="text-[var(--vh-text-secondary)]">{it.signer_title}</span></p>
                  <p className="text-xs text-[var(--vh-text-muted)] mt-1 font-mono">SHA-256 {it.file_sha256?.slice(0, 16)}… · {(it.file_size / 1024).toFixed(1)} KB · {it.organisation}</p>
                </div>
                <Pill tone={it.status === 'countersigned' ? 'success' : 'warn'}>{it.status}</Pill>
              </div>
              <div className="text-xs text-[var(--vh-text-secondary)] mt-3 grid sm:grid-cols-2 gap-2">
                <p><span className="text-[var(--vh-text-muted)]">Submitted:</span> {fmt(it.submitted_at)}</p>
                <p><span className="text-[var(--vh-text-muted)]">Counter-signed:</span> {fmt(it.countersigned_at)}</p>
                {it.notes && <p className="sm:col-span-2 italic text-[var(--vh-text-secondary)]">"{it.notes}"</p>}
                {it.countersign_notes && <p className="sm:col-span-2 italic text-[var(--vh-success)]">VH note: "{it.countersign_notes}"</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AIGovernanceTab() {
  return (
    <div className="space-y-4" data-testid="hub-ai">
      <div className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-6">
        <p className="text-xs uppercase tracking-[0.16em] text-[var(--vh-accent)] mb-2">EU AI Act Art. 14 · 22 · 72</p>
        <h3 className="font-display text-xl text-[var(--vh-text-primary)] mb-2">AI is governed by you, your users, and us — at the boundary.</h3>
        <p className="text-sm text-[var(--vh-text-secondary)] leading-relaxed">
          The full AI controls page (master switch, per-workflow toggles, gate decision log) is at <Link to="/admin/ai-controls" className="underline text-[var(--vh-brand-primary)]">/admin/ai-controls</Link>.
          Every user can additionally toggle AI off on their own account from <Link to="/settings/ai-preferences" className="underline text-[var(--vh-brand-primary)]">/settings/ai-preferences</Link>.
          Each decision is audit-logged for Article 72 post-market monitoring.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/admin/ai-controls" className="block bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-6 hover:shadow-md transition" data-testid="hub-ai-cta-admin">
          <p className="text-xs uppercase tracking-[0.16em] text-[var(--vh-accent)] mb-1">Admin scope</p>
          <h4 className="font-display text-lg text-[var(--vh-text-primary)] mb-1">Org-wide AI controls</h4>
          <p className="text-xs text-[var(--vh-text-secondary)]">Disable AI globally, per workflow. See the gate decision log.</p>
        </Link>
        <Link to="/settings/ai-preferences" className="block bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-6 hover:shadow-md transition" data-testid="hub-ai-cta-user">
          <p className="text-xs uppercase tracking-[0.16em] text-[var(--vh-accent)] mb-1">Personal scope</p>
          <h4 className="font-display text-lg text-[var(--vh-text-primary)] mb-1">My AI preferences</h4>
          <p className="text-xs text-[var(--vh-text-secondary)]">Disable AI on your own account. Honoured at the request boundary.</p>
        </Link>
      </div>
    </div>
  );
}

function AuditTab() {
  const [audit, setAudit] = useState(null);
  useEffect(() => {
    axios.get(`${API}/compliance-hub/audit?limit=200`).then((r) => setAudit(r.data)).catch(() => setAudit({ events: [], scope_company: '—' }));
  }, []);
  if (!audit) return <div className="text-sm text-[var(--vh-text-secondary)]">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="hub-audit">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-[var(--vh-text-secondary)]">Audit events scoped to: <strong className="text-[var(--vh-text-primary)]">{audit.scope_company}</strong></p>
        <Pill testId="hub-audit-count">{audit.events.length} events</Pill>
      </div>
      <div className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-2 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="text-[var(--vh-text-muted)] uppercase tracking-[0.12em] text-[10px]">
            <tr><th className="text-left p-3">When</th><th className="text-left p-3">Action</th><th className="text-left p-3">User</th><th className="text-left p-3">Details</th></tr>
          </thead>
          <tbody className="divide-y divide-[var(--vh-border)]">
            {audit.events.length === 0 && <tr><td colSpan="4" className="text-center p-6 text-[var(--vh-text-muted)]">No events.</td></tr>}
            {audit.events.map((ev, i) => (
              <tr key={`${ev.timestamp || i}-${i}`} className="text-[var(--vh-text-secondary)]">
                <td className="p-3 font-mono whitespace-nowrap">{fmt(ev.timestamp)}</td>
                <td className="p-3 font-mono text-[var(--vh-brand-primary)]">{ev.action}</td>
                <td className="p-3 font-mono text-[var(--vh-text-muted)]">{ev.user_id ? `${String(ev.user_id).slice(0, 8)}…` : '—'}</td>
                <td className="p-3 max-w-md truncate font-mono text-[var(--vh-text-muted)]" title={ev.details ? JSON.stringify(ev.details) : ''}>{ev.details ? JSON.stringify(ev.details).slice(0, 120) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DOC_CATEGORIES = ['dpia', 'fria', 'pen_test', 'cert', 'misc'];

function DocumentsTab() {
  const [docs, setDocs] = useState(null);
  const [form, setForm] = useState({ title: '', category: 'dpia', description: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = () => axios.get(`${API}/compliance-hub/documents`).then((r) => setDocs(r.data.documents)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault(); setErr('');
    if (!file) { setErr('Choose a file first.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', form.title);
      fd.append('category', form.category);
      fd.append('description', form.description);
      await axios.post(`${API}/compliance-hub/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm({ title: '', category: 'dpia', description: '' }); setFile(null);
      load();
    } catch (e) { setErr(e.response?.data?.detail || 'Upload failed.'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    await axios.delete(`${API}/compliance-hub/documents/${id}`); load();
  };

  return (
    <div className="space-y-5" data-testid="hub-documents">
      <form onSubmit={upload} className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5 grid gap-3" data-testid="hub-doc-form">
        <p className="text-xs uppercase tracking-[0.16em] text-[var(--vh-accent)]">Upload a compliance document</p>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="text-xs text-[var(--vh-text-secondary)]">Title
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="block w-full mt-1 px-3 py-2 rounded-lg border border-[var(--vh-border)] bg-[var(--vh-bg-base)] text-sm text-[var(--vh-text-primary)]" data-testid="hub-doc-title" />
          </label>
          <label className="text-xs text-[var(--vh-text-secondary)]">Category
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="block w-full mt-1 px-3 py-2 rounded-lg border border-[var(--vh-border)] bg-[var(--vh-bg-base)] text-sm text-[var(--vh-text-primary)]" data-testid="hub-doc-category">
              {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
        <label className="text-xs text-[var(--vh-text-secondary)]">Description (optional)
          <textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="block w-full mt-1 px-3 py-2 rounded-lg border border-[var(--vh-border)] bg-[var(--vh-bg-base)] text-sm text-[var(--vh-text-primary)]" data-testid="hub-doc-description" />
        </label>
        <label className="text-xs text-[var(--vh-text-secondary)]">File (PDF / DOCX / image · max 12 MB)
          <input type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files[0])} className="block mt-1 text-sm text-[var(--vh-text-primary)]" data-testid="hub-doc-file" />
        </label>
        <div className="flex items-center justify-between gap-3">
          {err && <span className="text-xs text-[var(--vh-accent)] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{err}</span>}
          <button type="submit" disabled={busy} className="ml-auto px-5 py-2 rounded-full bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)] text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="hub-doc-submit">
            <Upload className="w-4 h-4" /> {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>

      {!docs ? <div>Loading…</div> : (docs.length === 0 ? (
        <div className="bg-[var(--vh-bg-surface)] border border-dashed border-[var(--vh-border)] rounded-2xl p-10 text-center" data-testid="hub-doc-empty">
          <FolderUp className="w-6 h-6 mx-auto mb-3 text-[var(--vh-text-muted)]" />
          <p className="text-sm text-[var(--vh-text-secondary)]">No documents yet — upload your first DPIA / FRIA / cert above.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {docs.map((d) => (
            <div key={d.id} className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5">
              <div className="flex justify-between items-start gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-[var(--vh-text-primary)]">{d.title}</p>
                    <Pill tone="accent">{d.category}</Pill>
                  </div>
                  {d.description && <p className="text-xs text-[var(--vh-text-secondary)] mt-1">{d.description}</p>}
                  <p className="text-[11px] text-[var(--vh-text-muted)] mt-2 font-mono">{d.file_name} · {(d.file_size / 1024).toFixed(1)} KB · SHA-256 {d.file_sha256?.slice(0, 12)}… · {fmt(d.uploaded_at)}</p>
                </div>
                <button onClick={() => remove(d.id)} className="text-[var(--vh-accent)] hover:bg-[var(--vh-accent)]/10 p-2 rounded-lg" aria-label="Delete document" data-testid={`hub-doc-delete-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function SubProcessorChangesTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/compliance-hub/sub-processor-changes`).then((r) => setRows(r.data.changes)).catch(() => setRows([]));
  }, []);
  if (!rows) return <div className="text-sm text-[var(--vh-text-secondary)]">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="hub-subp">
      <div className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5">
        <p className="text-xs uppercase tracking-[0.16em] text-[var(--vh-accent)] mb-2">UK GDPR Art. 28(2)</p>
        <h3 className="font-display text-lg text-[var(--vh-text-primary)] mb-1">Sub-processor change-log</h3>
        <p className="text-sm text-[var(--vh-text-secondary)] leading-relaxed">14 days' notice before any new sub-processor is engaged. <a href="/legal/sub-processors.html" target="_blank" rel="noreferrer" className="underline text-[var(--vh-brand-primary)]">View the current list →</a></p>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[var(--vh-text-muted)] text-center py-6">No changes on record.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => (
            <div key={c.id} className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5">
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <p className="font-semibold text-[var(--vh-text-primary)]">{c.summary}</p>
                <Pill tone="neutral">{c.change_type}</Pill>
              </div>
              <p className="text-xs text-[var(--vh-text-secondary)] mt-2">{c.details}</p>
              <p className="text-[11px] text-[var(--vh-text-muted)] mt-2 font-mono">Effective {(c.effective_date || '').slice(0, 10)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ComplianceHubPage() {
  const [hub, setHub] = useState(null);
  const [tab, setTab] = useState('overview');
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/compliance-hub/me`).then((r) => setHub(r.data)).catch((e) => {
      setError(e.response?.data?.detail || 'Could not load the Compliance Hub.');
    });
  }, []);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="hub-access-denied">
        <div className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-9 text-center">
          <ShieldCheck className="w-7 h-7 mx-auto mb-4 text-[var(--vh-accent)]" />
          <h2 className="font-display text-2xl text-[var(--vh-text-primary)] mb-2">Access requires a designated role</h2>
          <p className="text-sm text-[var(--vh-text-secondary)] mb-5 leading-relaxed">
            The Compliance &amp; Security Hub is only available to users assigned a <strong>Data Protection Officer</strong>, <strong>Information Security</strong>, or <strong>Security Owner</strong> role for their organisation. Ask your Vault Hire administrator (or a member of your organisation already in the hub) to assign you a role.
          </p>
          <p className="text-xs text-[var(--vh-text-muted)] font-mono">{error}</p>
        </div>
      </div>
    );
  }

  if (!hub) return <div className="p-8 text-[var(--vh-text-secondary)]" data-testid="hub-loading">Loading Compliance Hub…</div>;

  const ActiveTab = (
    tab === 'overview' ? <OverviewTab hub={hub} /> :
    tab === 'agreements' ? <AgreementsTab /> :
    tab === 'ai' ? <AIGovernanceTab /> :
    tab === 'audit' ? <AuditTab /> :
    tab === 'documents' ? <DocumentsTab /> :
    <SubProcessorChangesTab />
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="compliance-hub-page">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-2">Compliance &amp; Security Hub</p>
        <h1 className="font-display text-3xl md:text-4xl text-[var(--vh-text-primary)] mb-2">{hub.viewer.company_name || 'All organisations'}</h1>
        <p className="text-[var(--vh-text-secondary)] flex items-center gap-2 text-sm flex-wrap">
          <CheckCircle2 className="w-4 h-4 text-[var(--vh-success)]" />
          Signed in as <strong>{hub.viewer.full_name || hub.viewer.email}</strong>
          {hub.viewer.compliance_role_label && <Pill tone="accent">{hub.viewer.compliance_role_label}</Pill>}
        </p>
      </header>

      <div className="flex gap-1 bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-full p-1 mb-6 overflow-x-auto" data-testid="hub-tabs">
        {TABS.map(({ id, label, Icon }) => {
          const active = tab === id;
          return (
            <button key={id} data-testid={`hub-tab-${id}`} onClick={() => setTab(id)} className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition ${active ? 'bg-[var(--vh-brand-primary)] text-white font-semibold' : 'text-[var(--vh-text-secondary)] hover:text-[var(--vh-brand-primary)]'}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          );
        })}
      </div>

      <div>{ActiveTab}</div>
    </div>
  );
}
